// Bilingual data for the two new onboarding preview screens
// (`OnboardingFeaturesScreen`, `OnboardingCompetitorsScreen`).
//
// Mirrors the `FEATURES` / `RIVAL_ROWS` arrays inside `LandingV2.jsx`
// so the mobile onboarding tells the exact same story as the web
// marketing page — but with lightweight text cards instead of the
// heavy web mocks that don't animate well on mid-range Android.
//
// COMPETITORS: intentionally does NOT name any competitor app. Owner
// requirement (Sep 30 2026) — the second column is generically
// labelled "التطبيقات الأخرى" / "Other apps" everywhere.
import {
  Camera, ChefHat, Import, ShoppingCart, Flame, BarChart3, Droplets,
  Dumbbell, Footprints, Scale, Share2, Bot, MessageCircle, NotebookPen,
} from "lucide-react";

// Colour aliases keep the card gradient consistent with the rest of
// the app's brand palette. Each key maps to a Tailwind text/bg pair.
const TONES = {
  brand:   { bg: "bg-brand-50",   text: "text-brand-600",   ring: "ring-brand-200" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-600", ring: "ring-emerald-200" },
  violet:  { bg: "bg-violet-50",  text: "text-violet-600",  ring: "ring-violet-200" },
  sky:     { bg: "bg-sky-50",     text: "text-sky-600",     ring: "ring-sky-200" },
  amber:   { bg: "bg-amber-50",   text: "text-amber-600",   ring: "ring-amber-200" },
  rose:    { bg: "bg-rose-50",    text: "text-rose-600",    ring: "ring-rose-200" },
};

export function toneClasses(name) {
  return TONES[name] || TONES.brand;
}

export const FEATURES_ONBOARD = [
  { icon: Camera,         tone: "brand",
    title_ar: "سجّل أكلك بـ 5 طرق ذكية",
    title_en: "Log food 5 smart ways",
    desc_ar:  "صور وجبتك · تكلم عن وش أكلت · امسح الباركود · اختر من مفضلتك · أو اكتب يدوي.",
    desc_en:  "Photo · voice · barcode · favorites · manual entry — Calofay figures out the rest." },
  { icon: ChefHat,        tone: "emerald",
    title_ar: "+500 وصفة عربية جاهزة",
    title_en: "500+ ready-to-cook recipes",
    desc_ar:  "مكتبة وصفات عربية كاملة مع المكونات والسعرات — من الفطور إلى العشاء والحلى.",
    desc_en:  "A full recipe library — ingredients, steps, and macros — for every meal of the day." },
  { icon: Import,         tone: "violet",
    title_ar: "استيراد وصفة من Instagram أو TikTok",
    title_en: "Import recipes from Instagram or TikTok",
    desc_ar:  "الصق رابط الريلز أو التيك توك واستخرج المكونات والخطوات والسعرات — بضغطة زر.",
    desc_en:  "Paste a Reels or TikTok link — get the ingredients, steps, and calories in one tap." },
  { icon: ShoppingCart,   tone: "amber",
    title_ar: "قائمة تسوق ذكية تلقائية",
    title_en: "Auto-built smart shopping list",
    desc_ar:  "أضف وصفاتك وخلي كالوفاي يجمع المكونات في قائمة مرتبة حسب أقسام السوبرماركت.",
    desc_en:  "Pick your recipes; Calofay bundles the ingredients into an aisle-sorted list." },
  { icon: Flame,          tone: "brand",
    title_ar: "السعرات",
    title_en: "Calories",
    desc_ar:  "اعرف كم سعرة تحتاج، كم أكلت، وكم باقي لك في اليوم — بعرض واضح ودائم.",
    desc_en:  "See how many calories you need, ate, and still have — always visible, always clear." },
  { icon: BarChart3,      tone: "emerald",
    title_ar: "الماكروز",
    title_en: "Macros",
    desc_ar:  "تابع البروتين والكارب والدهون كل يوم بطريقة مرئية وسهلة.",
    desc_en:  "Track protein, carbs, and fat every day with clean visual rings." },
  { icon: Droplets,       tone: "sky",
    title_ar: "الماء",
    title_en: "Water",
    desc_ar:  "حدد هدفك اليومي واضغط زر واحد لكل كوب — مع تذكيرات ذكية طول اليوم.",
    desc_en:  "Set a daily goal, tap once per glass, get smart reminders." },
  { icon: Dumbbell,       tone: "violet",
    title_ar: "التمارين",
    title_en: "Workouts",
    desc_ar:  "سجّل تمرينك واحسب السعرات المحروقة تلقائيًا — أكثر من 100 نوع تمرين.",
    desc_en:  "Log your workout and get calories burned auto-calculated — 100+ exercise types." },
  { icon: Footprints,     tone: "amber",
    title_ar: "الخطوات",
    title_en: "Steps",
    desc_ar:  "تابع خطواتك اليومية واربطها بتطبيق الصحة عالآيفون لتزامن تلقائي.",
    desc_en:  "Track daily steps; sync automatically with Apple Health." },
  { icon: Scale,          tone: "rose",
    title_ar: "الوزن",
    title_en: "Weight",
    desc_ar:  "سجّل وزنك بلمستين واشوف تغيّره بمخطط اتجاهي واضح — بدون تخمين.",
    desc_en:  "Log weight in two taps; watch a real trend line unfold — no more guessing." },
  { icon: Camera,         tone: "violet",
    title_ar: "صور قبل / بعد",
    title_en: "Before / after photos",
    desc_ar:  "احفظ صور تقدمك وقارنها جنب بعض داخل التطبيق — تشوف التغيير حتى لو الميزان ما يوضحه.",
    desc_en:  "Save progress photos and slide before/after side-by-side inside the app." },
  { icon: Share2,         tone: "violet",
    title_ar: "شارك تقدمك مع مدربك",
    title_en: "Share progress with your coach",
    desc_ar:  "أعطِ مدربك رابط خاص محمي بكلمة سر ليشوف وزنك وسعراتك وصورك.",
    desc_en:  "Send your coach a password-protected link to view your weight, calories, and photos." },
  { icon: Bot,            tone: "violet",
    title_ar: "مدرب كالوفاي بالذكاء الاصطناعي",
    title_en: "Your personal AI coach",
    desc_ar:  "دردشة مباشرة مدرّبة على هدفك وسعراتك — اسأل، اقترح وجبات، وبدائل تناسبك.",
    desc_en:  "A ChatGPT-style chat that knows your goal, calories, and macros — asks anything." },
  { icon: MessageCircle,  tone: "emerald",
    title_ar: "أخصائية تغذية على واتساب",
    title_en: "WhatsApp support",
    desc_ar:  "لما تحتاج دعم بشري، أخصائية التغذية تجاوبك مباشرة على واتساب عن أكلك وهدفك.",
    desc_en:  "Have a question? Our support team answers directly on WhatsApp." },
  { icon: NotebookPen,    tone: "amber",
    title_ar: "دفتر اليوم",
    title_en: "Daily journal",
    desc_ar:  "دوّن يومياتك وجوعك وطاقتك ومزاجك — واكتب قائمة مهام لتتبع التزامك.",
    desc_en:  "Journal your day — hunger, energy, mood — and track your consistency." },
];

// Differentiators only — features Calofay has that (both/either) of
// the big two calorie apps are missing. Second column is always
// "Other apps" — never named per owner requirement.
export const RIVALS_ONBOARD = [
  { ar: "واجهة عربية أصلية",                                 en: "Native Arabic interface" },
  { ar: "تسجيل الأكل بالصوت العربي",                          en: "Voice logging in Arabic" },
  { ar: "+500 وصفة عربية جاهزة",                              en: "500+ ready-to-cook Arabic recipes" },
  { ar: "استيراد وصفة من رابط Instagram أو TikTok",          en: "Import a recipe from an Instagram or TikTok link" },
  { ar: "إنشاء قائمة تسوق تلقائية من الوصفات",              en: "Auto-generated shopping list from your recipes" },
  { ar: "مساعد ذكاء اصطناعي عربي يعرف هدفك",              en: "AI coach that already knows your goal" },
  { ar: "أخصائية تغذية عبر واتساب",                          en: "Chat with our support team on WhatsApp" },
  { ar: "مقارنة صورك قبل وبعد بالتواريخ",                    en: "Compare before/after photos with dates" },
  { ar: "مشاركة تقدمك مع مدربك برابط محمي",                 en: "Share your progress with your coach via a protected link" },
  { ar: "دفتر يومي (يومياتك + مهامك)",                       en: "Daily journal (mood log + to-do list)" },
];
